import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Baby, Users, Calendar, BookOpen, Shield, Heart,
  Plus, Check, Clock, AlertCircle, ArrowLeft, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FamilyHealth = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [vaccineReminders, setVaccineReminders] = useState([]);
  const [educationContent, setEducationContent] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
  const [showAgeGroupModal, setShowAgeGroupModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      const opts = { withCredentials: true };

      const [overviewRes, vaccinesRes, educationRes] = await Promise.all([
        axios.get(`${API}/family-health/overview`, opts),
        axios.get(`${API}/family-health/vaccine-reminders`, opts),
        axios.get(`${API}/family-health/education`)
      ]);

      setOverview(overviewRes.data);
      setVaccineReminders(vaccinesRes.data.reminders || []);
      setEducationContent(educationRes.data);
    } catch (error) {
      console.error('Error loading family health data:', error);
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteVaccine = async (reminderId) => {
    try {
      await axios.patch(
        `${API}/family-health/vaccine-reminders/${reminderId}/complete`,
        {},
        { withCredentials: true }
      );

      toast.success(t('messages.updated'));
      loadData();
    } catch (error) {
      console.error('Error completing vaccine:', error);
      toast.error(t('messages.somethingWrong'));
    }
  };

  const handleAgeGroupClick = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
    setShowAgeGroupModal(true);
  };

  // Age group vaccine schedule data
  const ageGroupSchedules = {
    infants: {
      title: t('familyHealth.immunizations.infants'),
      icon: '👶',
      description: 'Recommended vaccines for babies 0-12 months',
      vaccines: [
        { name: 'Hepatitis B', timing: 'Birth, 1-2 months, 6-18 months' },
        { name: 'DTaP (Diphtheria, Tetanus, Pertussis)', timing: '2, 4, 6 months' },
        { name: 'Hib (Haemophilus influenzae type b)', timing: '2, 4, 6 months' },
        { name: 'Polio (IPV)', timing: '2, 4, 6-18 months' },
        { name: 'Pneumococcal (PCV13)', timing: '2, 4, 6, 12-15 months' },
        { name: 'Rotavirus', timing: '2, 4, 6 months' },
      ],
      notes: 'Always consult your pediatrician for personalized schedule'
    },
    toddlers: {
      title: t('familyHealth.immunizations.toddlers'),
      icon: '🧒',
      description: 'Recommended vaccines for children 1-3 years',
      vaccines: [
        { name: 'MMR (Measles, Mumps, Rubella)', timing: '12-15 months' },
        { name: 'Varicella (Chickenpox)', timing: '12-15 months' },
        { name: 'Hepatitis A', timing: '12-23 months (2 doses)' },
        { name: 'DTaP booster', timing: '15-18 months' },
        { name: 'Polio (IPV) booster', timing: 'If not completed' },
      ],
      notes: 'Keep vaccination records updated and easily accessible'
    },
    preschool: {
      title: t('familyHealth.immunizations.preschool'),
      icon: '👦',
      description: 'Recommended vaccines for children 4-6 years',
      vaccines: [
        { name: 'DTaP booster', timing: '4-6 years' },
        { name: 'Polio (IPV) booster', timing: '4-6 years' },
        { name: 'MMR booster', timing: '4-6 years' },
        { name: 'Varicella booster', timing: '4-6 years' },
      ],
      notes: 'These are typically required before kindergarten entry'
    },
    schoolAge: {
      title: t('familyHealth.immunizations.schoolAge'),
      icon: '🎒',
      description: 'Recommended vaccines for children 7-12 years',
      vaccines: [
        { name: 'Tdap booster', timing: '11-12 years' },
        { name: 'HPV (Human Papillomavirus)', timing: '11-12 years (2-3 doses)' },
        { name: 'Meningococcal', timing: '11-12 years' },
        { name: 'Flu vaccine', timing: 'Annually' },
      ],
      notes: 'Important for school attendance and travel'
    },
    teens: {
      title: t('familyHealth.immunizations.teens'),
      icon: '🧑',
      description: 'Recommended vaccines for teens 13-18 years',
      vaccines: [
        { name: 'Meningococcal booster', timing: '16 years' },
        { name: 'HPV (if not completed)', timing: 'Catch-up series if needed' },
        { name: 'Tdap booster', timing: 'If not received at 11-12' },
        { name: 'Flu vaccine', timing: 'Annually' },
        { name: 'COVID-19', timing: 'As recommended' },
      ],
      notes: 'Discuss travel vaccines if applicable'
    },
    adults: {
      title: t('familyHealth.immunizations.adults'),
      icon: '🧑‍🦰',
      description: 'Recommended vaccines for adults',
      vaccines: [
        { name: 'Tdap/Td booster', timing: 'Every 10 years' },
        { name: 'Flu vaccine', timing: 'Annually' },
        { name: 'COVID-19', timing: 'As recommended' },
        { name: 'Shingles', timing: '50+ years' },
        { name: 'Pneumococcal', timing: '65+ years' },
        { name: 'HPV', timing: 'Up to age 26 if not completed' },
      ],
      notes: 'Additional vaccines may be needed based on health conditions and travel'
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthloop-mint-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 md:top-16 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Heart className="h-6 w-6 text-pink-600" />
                  {t('familyHealth.title')}
                </h1>
                <p className="text-sm text-gray-600">{t('familyHealth.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('familyHealth.planner.addMember')}</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.family_members || 0}</p>
                </div>
                <Users className="h-12 w-12 text-pink-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('familyHealth.reminders.activeReminders')}</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.active_reminders || 0}</p>
                </div>
                <Calendar className="h-12 w-12 text-purple-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('familyHealth.immunizations.upcomingReminders')}</p>
                  <p className="text-3xl font-bold text-gray-900">{overview?.upcoming_vaccines || 0}</p>
                </div>
                <Shield className="h-12 w-12 text-blue-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto">
            <TabsTrigger value="overview">{t('familyHealth.tabImmunizations')}</TabsTrigger>
            <TabsTrigger value="education">{t('familyHealth.tabEducation')}</TabsTrigger>
            <TabsTrigger value="reminders">{t('familyHealth.tabReminders')}</TabsTrigger>
            <TabsTrigger value="planner">{t('familyHealth.tabPlanner')}</TabsTrigger>
          </TabsList>

          {/* Immunizations Tab */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('familyHealth.immunizations.title')}</CardTitle>
                <CardDescription>{t('familyHealth.immunizations.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Vaccine Reminders */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{t('familyHealth.immunizations.upcomingReminders')}</h3>
                    <Button size="sm" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('familyHealth.immunizations.addReminder')}
                    </Button>
                  </div>

                  {vaccineReminders.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">{t('familyHealth.immunizations.noReminders')}</p>
                      <Button className="mt-4" size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        {t('familyHealth.immunizations.addReminder')}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {vaccineReminders.map((reminder) => (
                        <Card key={reminder.id} className="border-2">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-gray-900">{reminder.vaccine_name}</h4>
                                  <Badge variant={reminder.status === 'completed' ? 'success' : 'default'}>
                                    {reminder.status}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                  <Clock className="h-4 w-4" />
                                  {t('familyHealth.immunizations.dueDate')}: {new Date(reminder.due_date).toLocaleDateString()}
                                </p>
                                {reminder.notes && (
                                  <p className="text-sm text-gray-500 mt-2">{reminder.notes}</p>
                                )}
                              </div>
                              {reminder.status === 'pending' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleCompleteVaccine(reminder.id)}
                                  className="ml-4"
                                >
                                  <Check className="h-4 w-4 mr-2" />
                                  {t('familyHealth.immunizations.markComplete')}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Important Notice */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-900">
                        {t('familyHealth.immunizations.consultDoctor')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Age-Based Schedule */}
            <Card>
              <CardHeader>
                <CardTitle>{t('familyHealth.immunizations.recommendedSchedule')}</CardTitle>
                <CardDescription>{t('familyHealth.immunizations.byAge')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'infants', icon: '👶' },
                    { key: 'toddlers', icon: '🧒' },
                    { key: 'preschool', icon: '👦' },
                    { key: 'schoolAge', icon: '🎒' },
                    { key: 'teens', icon: '🧑' },
                    { key: 'adults', icon: '🧑‍🦰' }
                  ].map(({ key, icon }) => (
                    <Card 
                      key={key} 
                      className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-healthloop-mint-400"
                      onClick={() => handleAgeGroupClick(key)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{icon}</span>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {t(`familyHealth.immunizations.${key}`)}
                            </h4>
                            <p className="text-sm text-healthloop-mint-600 hover:underline">
                              {t('familyHealth.immunizations.learnMore')} →
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Education Tab */}
          <TabsContent value="education" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('familyHealth.education.title')}</CardTitle>
                <CardDescription>{t('familyHealth.education.subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {educationContent?.categories?.map((category) => (
                    <Card key={category.id} className="border-2 hover:shadow-lg transition-shadow cursor-pointer">
                      <CardHeader>
                        <CardTitle className="text-lg">{category.title}</CardTitle>
                        <CardDescription>{category.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {category.topics.map((topic, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                              <Check className="h-4 w-4 text-healthloop-mint-600 flex-shrink-0 mt-0.5" />
                              {topic}
                            </li>
                          ))}
                        </ul>
                        <Button variant="ghost" className="mt-4 w-full text-healthloop-mint-600">
                          {t('familyHealth.education.readMore')} →
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Popular Topics */}
                {educationContent?.popular_topics && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">{t('familyHealth.education.popularTopics')}</h3>
                    <div className="space-y-3">
                      {educationContent.popular_topics.map((topic, idx) => (
                        <Card key={idx} className="cursor-pointer hover:shadow-md transition-shadow">
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-gray-900">{topic.title}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline">{topic.category}</Badge>
                                  <span className="text-xs text-gray-500">
                                    {topic.read_time} {t('familyHealth.education.readTime')}
                                  </span>
                                </div>
                              </div>
                              <BookOpen className="h-5 w-5 text-gray-400" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reminders Tab */}
          <TabsContent value="reminders">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('familyHealth.reminders.title')}</CardTitle>
                    <CardDescription>{t('familyHealth.reminders.subtitle')}</CardDescription>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('familyHealth.reminders.createReminder')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">{t('familyHealth.reminders.noActiveReminders')}</p>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('familyHealth.reminders.createReminder')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Planner Tab */}
          <TabsContent value="planner">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('familyHealth.planner.title')}</CardTitle>
                    <CardDescription>{t('familyHealth.planner.subtitle')}</CardDescription>
                  </div>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('familyHealth.planner.addMember')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-dashed border-purple-300">
                  <Users className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium mb-2">{t('familyHealth.planner.nothingScheduled')}</p>
                  <p className="text-gray-600 text-sm mb-4">
                    {t('familyHealth.planner.upcomingCare')}
                  </p>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('familyHealth.planner.scheduleCheckup')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Age Group Information Modal */}
      <Dialog open={showAgeGroupModal} onOpenChange={setShowAgeGroupModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedAgeGroup && ageGroupSchedules[selectedAgeGroup] && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-5xl">{ageGroupSchedules[selectedAgeGroup].icon}</span>
                  <div>
                    <DialogTitle className="text-2xl">
                      {ageGroupSchedules[selectedAgeGroup].title}
                    </DialogTitle>
                    <DialogDescription className="text-base mt-1">
                      {ageGroupSchedules[selectedAgeGroup].description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">
                    Recommended Vaccines
                  </h3>
                  <div className="space-y-3">
                    {ageGroupSchedules[selectedAgeGroup].vaccines.map((vaccine, idx) => (
                      <Card key={idx} className="border-2 border-healthloop-mint-100 bg-healthloop-mint-50/30">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3">
                            <Shield className="h-5 w-5 text-healthloop-mint-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{vaccine.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">Timing:</span> {vaccine.timing}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Important Note */}
                <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900 mb-1">Important Note</p>
                      <p className="text-sm text-blue-800">
                        {ageGroupSchedules[selectedAgeGroup].notes}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-900">
                    <strong>Disclaimer:</strong> This information is for educational purposes only. 
                    Always consult with your healthcare provider for personalized vaccine recommendations 
                    based on your child's health history and current guidelines.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setShowAgeGroupModal(false);
                      // Could navigate to add reminder or other action
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Vaccine Reminder
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowAgeGroupModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyHealth;
